import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

/**
 * Friendly "you don't have access" screen (spec §2.1).
 *
 * Shown when a user reaches a route their roles/entitlements don't grant. Menu
 * gating (§3.2) normally prevents this, so it's the fallback for direct URL
 * navigation. Self-contained + theme-aware so it needs no extra stylesheet.
 */
const AccessDenied = ({
  title = "You don't have access to this feature",
  message = 'Your current roles don’t include this area, or it isn’t enabled for your company. Please contact your administrator if you think this is a mistake.',
}) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const colors = isDarkMode
    ? {
        text: '#f1f5f9',
        subtext: '#94a3b8',
        iconBg: 'rgba(248, 113, 113, 0.12)',
        icon: '#f87171',
        btnBg: '#6366f1',
        btnText: '#ffffff',
      }
    : {
        text: '#0f172a',
        subtext: '#64748b',
        iconBg: 'rgba(239, 68, 68, 0.10)',
        icon: '#dc2626',
        btnBg: '#4f46e5',
        btnText: '#ffffff',
      };

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: colors.iconBg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          <ShieldAlert size={34} color={colors.icon} strokeWidth={2} />
        </div>
        <h1
          style={{
            margin: '0 0 12px',
            fontSize: 22,
            fontWeight: 700,
            color: colors.text,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            margin: '0 0 28px',
            fontSize: 15,
            lineHeight: 1.6,
            color: colors.subtext,
          }}
        >
          {message}
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            background: colors.btnBg,
            color: colors.btnText,
            border: 'none',
            borderRadius: 10,
            padding: '10px 22px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Go to Home
        </button>
      </div>
    </div>
  );
};

export default AccessDenied;
